import { useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  ChevronRight,
  Copy,
  Pencil,
  Settings,
  UserPlus,
  RotateCcw,
} from "lucide-react";

import { Card, CardHeader, CardBody } from "../../components/Card/Card";
import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Menu } from "../../components/Menu/Menu";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { useToast } from "../../components/Toast/Toast";
import { useAuth } from "../auth/useAuth";
import { getTeam, TeamMember } from "../team/api";
import { InviteMemberModal } from "../team/InviteMemberModal";
import {
  getProject,
  suspendProject,
  unsuspendProject,
  ProjectDetail,
} from "./api";
import { getApiKey } from "../apikeys/api";
import { AnalyticsCards } from "./AnalyticsCards";
import { RenameProjectModal } from "./RenameProjectModal";
import { DeleteProjectModal } from "./DeleteProjectModal";
import { ActivityLogCard } from "./ActivityLog/ActivityLogCard";
import { ApiError, describeError } from "../../lib/errors";
import styles from "./ProjectOverviewPage.module.css";

// Date formatter — matches mockup "Oct 12, 2023" copy (L459).
const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

// Mockup hero ID code chip: `e74c…a82f` (L461). For non-UUIDish IDs we just
// show the first 8 chars to avoid a misleading ellipsis.
function shortId(id: string): string {
  if (!id) return "";
  if (id.length <= 9) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
}

// Slightly longer Project-ID code chip in the Project details card — mockup
// uses `e74c9d10-…a82f` style (L562).
function midId(id: string): string {
  if (!id) return "";
  if (id.length <= 14) return id;
  return `${id.slice(0, 8)}-…${id.slice(-4)}`;
}

// Friendly "Last login" formatter. Falls back to absolute date for dates older
// than 30 days. Used in the Team members preview rows (mockup L491-L516).
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "—";
  const now = Date.now();
  const diff = Math.max(0, now - ts);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return DATE_FMT.format(new Date(ts));
}

export function ProjectOverviewPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isSuperAdmin = user?.role === "super_admin";

  const [renameOpen, setRenameOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Primary project fetch — also exposes the ETag for the rename flow.
  const projectQ = useQuery({
    queryKey: ["project", id],
    queryFn: () =>
      getProject(id).then((r) => ({
        project: r.data,
        etag: r.etag ?? r.data.etag,
      })),
    enabled: !!id,
  });

  // Top-4 team members for the preview card. The backend's team endpoint
  // returns all members; we slice on the client (it's small) and surface
  // failures silently — an EmptyState is rendered in that case.
  const teamQ = useQuery({
    queryKey: ["team", id],
    queryFn: () => getTeam(id),
    enabled: !!id && !!projectQ.data,
  });

  const data: ProjectDetail | undefined = projectQ.data?.project;
  const etag = projectQ.data?.etag ?? null;

  function onStatusError(err: unknown) {
    push({
      variant: "danger",
      message:
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
    });
  }

  const suspendMut = useMutation({
    mutationFn: () => suspendProject(id),
    onSuccess: async () => {
      push({ variant: "success", message: "Project suspended." });
      await qc.invalidateQueries({ queryKey: ["project", id] });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
    },
    onError: onStatusError,
  });

  const unsuspendMut = useMutation({
    mutationFn: () => unsuspendProject(id),
    onSuccess: async () => {
      push({ variant: "success", message: "Project reactivated." });
      await qc.invalidateQueries({ queryKey: ["project", id] });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
    },
    onError: onStatusError,
  });

  const breadcrumb = [
    { label: "All projects", to: "/projects" },
    { label: data?.name ?? "Project" },
  ];

  const isSuspended = data?.status === "suspended";

  const headerActions = (
    <>
      {/*
        Analytics window is fixed at 7 days in v1 — a static chip, no dropdown
        affordance. See docs/mockups/project-overview.html.
      */}
      <span className={styles.rangeChip}>
        <Calendar size={13} aria-hidden="true" />
        <span>Last 7 days</span>
      </span>
      {isSuperAdmin && (
        <Menu
          aria-label="More project actions"
          items={[
            isSuspended
              ? {
                  label: "Unsuspend project",
                  disabled: !data,
                  onSelect: () => unsuspendMut.mutate(),
                }
              : {
                  label: "Suspend project",
                  variant: "danger",
                  disabled: !data,
                  onSelect: () => suspendMut.mutate(),
                },
            { separator: true },
            {
              label: "Delete project",
              variant: "danger",
              disabled: !data,
              onSelect: () => setDeleteOpen(true),
            },
          ]}
        />
      )}
    </>
  );

  const header = (
    <PageHeader
      breadcrumb={breadcrumb}
      title="Overview"
      actions={headerActions}
    />
  );

  if (projectQ.isLoading) {
    return (
      <>
        {header}
        <div className={styles.page}>Loading…</div>
      </>
    );
  }
  if (projectQ.error) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <Alert variant="danger" title="Couldn't load project">
            {projectQ.error instanceof ApiError
              ? describeError(projectQ.error.code)
              : describeError("internal")}
          </Alert>
        </div>
      </>
    );
  }
  if (!data) return header;

  return (
    <>
      {header}
      <div className={styles.page}>
        <HeroCard
          project={data}
          canRename={isSuperAdmin}
          onRename={() => setRenameOpen(true)}
        />

        {data.status === "suspended" && (
          <div className={styles.suspendedAlert}>
            <Alert variant="warn" title="This project is suspended.">
              Players can&apos;t authenticate or use social features until it&apos;s
              reactivated.
            </Alert>
          </div>
        )}

        {/* Phase 2 — game-level analytics. See docs/mockups/project-overview.html. */}
        <AnalyticsCards projectId={id} />

        <div className={styles.grid}>
          {/* Left column: Team members + Activity log */}
          <div className={styles.column}>
            <TeamMembersCard
              projectId={id}
              members={teamQ.data?.members}
              error={teamQ.error}
              loading={teamQ.isLoading}
              onInviteClick={() => setInviteOpen(true)}
            />
            <ActivityLogCard projectId={id} />
          </div>

          {/* Right column: API key + Project details + Quick links */}
          <div className={styles.column}>
            <ProductionApiKeyCard projectId={id} />
            <ProjectDetailsCard
              project={data}
              onCopyId={async () => {
                try {
                  await navigator.clipboard.writeText(data.id);
                  push({ variant: "success", message: "Project ID copied." });
                } catch {
                  push({
                    variant: "danger",
                    message: "Couldn't copy — clipboard unavailable.",
                  });
                }
              }}
            />
            <QuickLinksCard
              projectId={id}
              onInviteClick={() => setInviteOpen(true)}
              onConfigClick={() => navigate(`/projects/${id}/config`)}
            />
          </div>
        </div>

        {renameOpen && (
          <RenameProjectModal
            open={renameOpen}
            onClose={() => setRenameOpen(false)}
            project={data}
            etag={etag}
          />
        )}

        {deleteOpen && (
          <DeleteProjectModal
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            project={{
              id: data.id,
              name: data.name,
              etag: etag ?? data.etag,
            }}
            onDeleted={() => {
              setDeleteOpen(false);
              push({ variant: "success", message: "Project deleted." });
              qc.invalidateQueries({ queryKey: ["projects"] });
              navigate("/projects");
            }}
          />
        )}

        <InviteMemberModal
          projectId={id}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
        />
      </div>
    </>
  );
}

/* ===== Hero card (mockup L451-L465) ===== */

function HeroCard({
  project,
  canRename,
  onRename,
}: {
  project: ProjectDetail;
  canRename: boolean;
  onRename: () => void;
}) {
  const memberLabel = `${project.members_count} team ${
    project.members_count === 1 ? "member" : "members"
  }`;
  const created = DATE_FMT.format(new Date(project.created_at));
  return (
    <div className={styles.hero}>
      <div className={styles.heroIcon}>
        <Avatar name={project.name} size={56} />
      </div>
      <div className={styles.heroInfo}>
        <h2 className={styles.heroName}>
          {project.name}
          {/* Real status from the backend — Active or Suspended (L671). */}
          {project.status === "suspended" ? (
            <Badge variant="danger">Suspended</Badge>
          ) : (
            <Badge variant="success">Active</Badge>
          )}
        </h2>
        <div className={styles.heroMeta}>
          <span>
            <strong>{project.members_count}</strong>{" "}
            {project.members_count === 1 ? "team member" : "team members"}
          </span>
          <span className={styles.sep}>·</span>
          <span>Created {created}</span>
          <span className={styles.sep}>·</span>
          <span>
            ID <code>{shortId(project.id)}</code>
          </span>
          {/* Hidden helper for testing-library — gives a stable role-less hit */}
          <span aria-hidden hidden>
            {memberLabel}
          </span>
        </div>
      </div>
      {canRename && (
        <div className={styles.heroActions}>
          <Button variant="secondary" size="sm" onClick={onRename}>
            <Pencil size={14} aria-hidden="true" />
            <span>Rename</span>
          </Button>
        </div>
      )}
    </div>
  );
}

/* ===== Team members card (mockup L481-L520) ===== */

function TeamMembersCard({
  projectId,
  members,
  error,
  loading,
  onInviteClick,
}: {
  projectId: string;
  members: TeamMember[] | undefined;
  error: unknown;
  loading: boolean;
  onInviteClick: () => void;
}) {
  const top = useMemo(() => (members ?? []).slice(0, 4), [members]);
  const count = members?.length ?? 0;

  return (
    <Card>
      <CardHeader>
        <h3>Team members</h3>
        <span className={styles.cardHeaderRight}>
          {count > 0 && (
            <Link
              className={styles.viewAll}
              to={`/projects/${projectId}/team`}
            >
              View all ({count}) →
            </Link>
          )}
        </span>
      </CardHeader>
      <CardBody>
        {loading && <div className={styles.dim}>Loading members…</div>}
        {!loading && !!error && (
          <EmptyState
            title="Couldn't load team"
            description="Refresh the page to try again."
          />
        )}
        {!loading && !error && top.length === 0 && (
          <EmptyState
            title="No team members yet"
            description="Invite teammates to collaborate on this project."
            action={
              <Button onClick={onInviteClick}>
                <UserPlus size={14} aria-hidden="true" />
                <span>Invite member</span>
              </Button>
            }
          />
        )}
        {!loading && !error && top.length > 0 && (
          <div className={styles.memberList}>
            {top.map((m) => (
              <div key={m.user_id} className={styles.memberRow}>
                <Avatar name={m.name || m.email} size={32} />
                <span className={styles.memberInfo}>
                  <span className={styles.memberName}>{m.email}</span>
                  <span className={styles.memberMeta}>
                    Joined · {formatRelative(m.joined_at)}
                  </span>
                </span>
                {/*
                  Backend "status" field encodes membership state, not
                  role. The mockup distinguishes "Super admin" vs "Member";
                  without a per-membership role we fall back to "Member" and
                  defer the admin highlight to when the API exposes it.
                */}
                <Badge variant="user">Member</Badge>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ===== Production API key card (mockup L541-L556) ===== */

function ProductionApiKeyCard({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  // Current API key read-state. Degrades gracefully — while loading or on a
  // 404/error the card still renders with em-dash placeholders.
  const apiKeyQ = useQuery({
    queryKey: ["project", projectId, "api-keys"],
    queryFn: () => getApiKey(projectId),
    enabled: !!projectId,
  });
  const apiKey = apiKeyQ.data;
  const last4 = apiKey?.last4 ?? "????";
  const rotatedDisplay = apiKey?.rotated_at
    ? DATE_FMT.format(new Date(apiKey.rotated_at))
    : apiKey
      ? "Never"
      : "—";
  const rotatedByDisplay = apiKey?.rotated_by?.email ?? "—";
  return (
    <Card>
      <CardHeader>
        <h3>Production API key</h3>
        <Badge variant="success" dot>
          Active
        </Badge>
      </CardHeader>
      <CardBody>
        <div className={styles.fpDisplay}>
          <span className={styles.fpDots}>••••••••••••••••••••••••</span>
          <span className={styles.fpTail}>{last4}</span>
        </div>
        <div className={styles.fpMeta}>
          <div>
            <div className="k">Rotated</div>
            <div className="v">{rotatedDisplay}</div>
          </div>
          <div>
            <div className="k">By</div>
            <div className="v">{rotatedByDisplay}</div>
          </div>
        </div>
        <Button
          variant="secondary"
          className={styles.rotateBtn}
          onClick={() => navigate(`/projects/${projectId}/api-keys`)}
        >
          <RotateCcw size={13} aria-hidden="true" />
          <span>Rotate or manage</span>
        </Button>
      </CardBody>
    </Card>
  );
}

/* ===== Project details card (mockup L558-L569) ===== */

function ProjectDetailsCard({
  project,
  onCopyId,
}: {
  project: ProjectDetail;
  onCopyId: () => void;
}) {
  const created = DATE_FMT.format(new Date(project.created_at));
  return (
    <Card>
      <CardHeader>
        <h3>Project details</h3>
      </CardHeader>
      <CardBody>
        <div className={styles.kvList}>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Project ID</span>
            <span className={styles.kvValue}>
              <code title={project.id}>{midId(project.id)}</code>
              <button
                type="button"
                className={styles.copyBtn}
                aria-label="Copy project ID"
                onClick={onCopyId}
              >
                <Copy size={12} aria-hidden="true" />
              </button>
            </span>
          </div>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Created</span>
            <span className={styles.kvValue}>{created}</span>
          </div>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Created by</span>
            <span className={styles.kvValue}>
              {project.created_by?.email ? (
                project.created_by.email
              ) : (
                <span className={styles.dim}>—</span>
              )}
            </span>
          </div>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Pending invites</span>
            <span className={styles.kvValue}>
              {project.pending_invites_count}
              <Link
                to={`/projects/${project.id}/team`}
                className={styles.inlineLink}
                aria-label="View team & invites"
              >
                View
              </Link>
            </span>
          </div>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Status</span>
            <span className={styles.kvValue}>
              {project.status === "suspended" ? (
                <Badge variant="danger">Suspended</Badge>
              ) : (
                <Badge variant="success">Active</Badge>
              )}
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/* ===== Quick links card (mockup L571-L597) ===== */

function QuickLinksCard({
  projectId,
  onInviteClick,
  onConfigClick,
}: {
  projectId: string;
  onInviteClick: () => void;
  onConfigClick: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <h3>Quick links</h3>
      </CardHeader>
      <CardBody>
        <Link
          to={`/projects/${projectId}/config`}
          className={styles.quickLink}
          onClick={(e) => {
            // Allow modifier-clicks to fall through to the Link's default nav.
            if (e.metaKey || e.ctrlKey || e.shiftKey) return;
            e.preventDefault();
            onConfigClick();
          }}
        >
          <span className={styles.quickTile} aria-hidden="true">
            <Settings size={16} />
          </span>
          <span className={styles.quickInfo}>
            <span className={styles.quickName}>Project config</span>
            <span className={styles.quickMeta}>
              Chat, guild, help requests…
            </span>
          </span>
          <ChevronRight
            size={14}
            aria-hidden="true"
            className={styles.quickChev}
          />
        </Link>
        <button
          type="button"
          className={styles.quickLink}
          onClick={onInviteClick}
        >
          <span className={styles.quickTile} aria-hidden="true">
            <UserPlus size={16} />
          </span>
          <span className={styles.quickInfo}>
            <span className={styles.quickName}>Invite member</span>
            <span className={styles.quickMeta}>
              Add someone to this project
            </span>
          </span>
          <ChevronRight
            size={14}
            aria-hidden="true"
            className={styles.quickChev}
          />
        </button>
      </CardBody>
    </Card>
  );
}
